import { useEffect, useMemo, useState } from "react";
import { Search, Users } from "lucide-react";
import { useCustomerChatStore } from "../../../store/customerChatStore";
import { useAuth } from "../../../context/AuthContext";
import { dictionary } from "../../../utils/dictionary";
import { decodeTokenService } from "../../../services/decodeTokenService";
import CustomerCard from "./CustomerCard";
import {
  getContactCount,
  listContactTags,
  type ContactCount,
} from "../../../services/whatsapp/whatsappService";
import { handleApi } from "../../../utils/handleApi";

const CustomerConversationList = () => {
  const { language } = useAuth();
  const {
    customers,
    customersLoading,
    searchQuery,
    setSearchQuery,
    selectCustomer,
    selectedPhone,
    fetchCustomers,
  } = useCustomerChatStore();

  const [contactCount, setContactCount] = useState<ContactCount | null>(null);
  // Tag filters use a Set so the user can keep more than one selected at the
  // same time. An empty set means "All" (no tag filter applied).
  const [activeTags, setActiveTags] = useState<Set<string>>(new Set());
  const [ownerTags, setOwnerTags] = useState<string[]>([]);

  useEffect(() => {
    const ownerId = decodeTokenService.getUserIdFromToken();
    if (ownerId) {
      fetchCustomers(ownerId);
    }
    loadCount();
    loadTags();
  }, []);

  const loadCount = async () => {
    const { res } = await handleApi(getContactCount(), false);
    if (res) setContactCount(res);
  };

  const loadTags = async () => {
    const { res } = await handleApi(listContactTags(), false);
    if (res) setOwnerTags(res);
  };

  const availableTags = useMemo(() => {
    const fromCustomers = new Set<string>();
    customers.forEach((c) => (c.tags || []).forEach((t) => fromCustomers.add(t)));
    return Array.from(new Set([...ownerTags, ...fromCustomers])).sort();
  }, [ownerTags, customers]);

  const filtered = customers.filter((c) => {
    if (activeTags.size > 0) {
      // OR semantics: show the customer if they carry any of the active tags.
      const customerTags = c.tags || [];
      const hasMatch = customerTags.some((t) => activeTags.has(t));
      if (!hasMatch) return false;
    }
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    const display = c.display_name || c.customer_name || "";
    return display.toLowerCase().includes(q) || c.customer_phone.includes(q);
  });

  const toggleTag = (tag: string) => {
    setActiveTags((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  };

  const handleSelect = (phone: string) => {
    const ownerId = decodeTokenService.getUserIdFromToken();
    if (ownerId) selectCustomer(phone, ownerId);
  };

  return (
    <>
      <div className="px-4 pt-4 pb-3 border-b border-gray-100">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-[#111B21]">
            {dictionary["chat-inbox-title"][language]}
          </h2>
        </div>

        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder={dictionary["chat-search-placeholder"][language]}
            className="w-full pl-9 pr-3 py-2 text-xs border border-gray-200 rounded-lg bg-[#F0F2F5] focus:outline-none focus:ring-1 focus:ring-[#00A884]/40 focus:border-[#00A884] focus:bg-white transition-colors"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="flex flex-wrap gap-1.5">
          <button
            onClick={() => setActiveTags(new Set())}
            className={`px-2.5 py-1 text-[11px] rounded-full font-medium transition-colors ${
              activeTags.size === 0
                ? "bg-[#00A884] text-white"
                : "bg-[#F0F2F5] text-[#667781] hover:bg-gray-200"
            }`}
          >
            {dictionary["chat-filter-all"][language]}
          </button>
          {availableTags.map((tag) => {
            const selected = activeTags.has(tag);
            return (
              <button
                key={tag}
                onClick={() => toggleTag(tag)}
                className={`px-2.5 py-1 text-[11px] rounded-full font-medium transition-colors ${
                  selected
                    ? "bg-[#00A884] text-white"
                    : "bg-[#F0F2F5] text-[#667781] hover:bg-gray-200"
                }`}
              >
                {tag}
              </button>
            );
          })}
          {availableTags.length === 0 && (
            <span className="text-[10px] text-gray-400 italic py-1">
              {dictionary["chat-filter-no-tags"]?.[language] ?? "Crea etiquetas desde el panel de contacto"}
            </span>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0">
        {customersLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="w-6 h-6 border-2 border-gray-300 border-t-[#00A884] rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-[#667781] py-12 text-xs px-4">
            <div className="w-12 h-12 bg-[#F0F2F5] rounded-full flex items-center justify-center mx-auto mb-3">
              <Search className="w-5 h-5 text-gray-300" />
            </div>
            {dictionary["chat-no-conversations"][language]}
          </div>
        ) : (
          filtered.map((customer) => (
            <CustomerCard
              key={customer.customer_phone}
              customer={customer}
              isSelected={selectedPhone === customer.customer_phone}
              onClick={() => handleSelect(customer.customer_phone)}
            />
          ))
        )}
      </div>

      {contactCount && (
        <div
          className={`flex items-center gap-2 px-4 py-2.5 border-t border-gray-200 text-xs flex-shrink-0 ${
            contactCount.used >= contactCount.limit * 0.9
              ? "bg-red-50 text-red-700"
              : "bg-[#F0F2F5] text-[#667781]"
          }`}
        >
          <Users className="w-3.5 h-3.5" />
          <span className="font-medium">
            {contactCount.used} / {contactCount.limit}
          </span>
          <span className="opacity-75">
            {dictionary["chat-contact-counter-label"]?.[language] ?? "contactos"}
          </span>
        </div>
      )}
    </>
  );
};

export default CustomerConversationList;
